import type { Content, JSONContent } from '@tiptap/react';

const isProseMirrorDoc = (value: unknown): value is JSONContent =>
  Boolean(value && typeof value === 'object' && (value as JSONContent).type === 'doc');

/** Unwraps `{ content: { type: 'doc', ... } }` or returns the doc node as-is. */
export const unwrapProseMirrorDoc = (value: unknown): JSONContent => {
  if (isProseMirrorDoc(value)) return value;

  if (value && typeof value === 'object' && 'content' in value) {
    const inner = (value as JSONContent).content;
    if (isProseMirrorDoc(inner)) return inner;
  }

  return { type: 'doc', content: [] };
};

const normalizeNode = (node: JSONContent): JSONContent => {
  const normalized: JSONContent = { ...node };

  if (normalized.type === 'heading') {
    const level = typeof normalized.attrs?.level === 'number' ? normalized.attrs.level : 2;
    normalized.attrs = { ...normalized.attrs, level };
  }

  if (Array.isArray(normalized.content)) {
    normalized.content = normalized.content.map((child) =>
      child && typeof child === 'object' && 'type' in child
        ? normalizeNode(child as JSONContent)
        : child
    );
  }

  return normalized;
};

const normalizeDoc = (value: JSONContent): JSONContent => {
  if (value.type === 'doc' && Array.isArray(value.content)) {
    return { type: 'doc', content: value.content.map(normalizeNode) };
  }

  if (Array.isArray(value.content)) {
    return { type: 'doc', content: value.content.map(normalizeNode) };
  }

  return { type: 'doc', content: [] };
};

export const normalizeProseMirrorContent = (content: Content): Content => {
  if (typeof content === 'string' || content == null) return content || '';
  return normalizeDoc(unwrapProseMirrorDoc(content));
};
